import { documentEntryHasLinkedGuide } from '@skill-trees/shared';
import { zipSync, strToU8 } from 'fflate';
import { TreeDataPersistor } from '../persistence/Persistor';

/** Renderer assets the artifact carries, fetched from the running site's `/embed/`. */
const RENDERER_ASSETS = [
    'index.html',
    'skill-tree-viewer.js',
    'skill-tree-viewer.css',
] as const;

const EMBED_BASE_PATH = '/embed';

export type DeployPhase = 'idle' | 'working' | 'error';

export interface DeployState {
    phase: DeployPhase;
    /** What the deploy is doing right now, for the button label. */
    step: string | null;
    error: string | null;
}

export interface DeployManifest {
    treeId: string;
    name: string;
    exportedAt: string;
    rendererBuildId: string;
    dataVersion: number;
    guideCount: number;
    /** Leaves whose guide the tree declares but whose markdown could not be read. */
    missingGuides: string[];
}

export interface DeployResult {
    zip: Uint8Array;
    fileName: string;
    manifest: DeployManifest;
}

export interface DeployInteractorOptions {
    persistor: TreeDataPersistor;
    treeId: string;
    /** Defaults to the page's `fetch`; injected in tests. */
    fetchFn?: typeof fetch;
    /** Origin the renderer assets are fetched from; defaults to the current page's. */
    origin?: string;
    /** Compiled-in build id of the running renderer. */
    rendererBuildId?: string;
    now?: () => Date;
}

/**
 * Assembles a deployable artifact for one tree, entirely in the browser with the
 * editor's existing session: the published tree, the markdown for every leaf that
 * declares a guide, the prebuilt renderer from this site's `/embed/`, a manifest and a
 * host README, zipped under a `{treeId}/` folder.
 *
 * Returns bytes rather than triggering a download — jsdom has no `URL.createObjectURL`,
 * so the anchor-and-object-URL dance stays in the component where it is untestable but
 * trivial, and everything that decides what goes in the zip stays testable here.
 */
export class DeployInteractor {
    private state: DeployState = { phase: 'idle', step: null, error: null };
    private updateCallback?: (state: DeployState) => void;

    private readonly persistor: TreeDataPersistor;
    private readonly treeId: string;
    private readonly fetchFn: typeof fetch;
    private readonly origin: string;
    private readonly rendererBuildId: string;
    private readonly now: () => Date;

    constructor(options: DeployInteractorOptions) {
        this.persistor = options.persistor;
        this.treeId = options.treeId;
        this.fetchFn = options.fetchFn ?? ((...args) => fetch(...args));
        this.origin =
            options.origin ?? (typeof window !== 'undefined' ? window.location.origin : '');
        this.rendererBuildId = options.rendererBuildId ?? 'unknown';
        this.now = options.now ?? (() => new Date());
    }

    setUpdateCallback(callback: (state: DeployState) => void): void {
        this.updateCallback = callback;
    }

    getState(): DeployState {
        return { ...this.state };
    }

    private setState(updates: Partial<DeployState>): void {
        this.state = { ...this.state, ...updates };
        this.updateCallback?.(this.getState());
    }

    /**
     * Builds the artifact. Resolves to `null` on failure, with the reason in
     * {@link getState}; the caller shows it rather than throwing at the user.
     */
    async deploy(): Promise<DeployResult | null> {
        if (this.state.phase === 'working') {
            return null;
        }
        this.setState({ phase: 'working', step: 'Loading tree…', error: null });

        try {
            // The published tree, not the editor's draft: `loadData` goes to `load-tree`,
            // which returns the published file even when called from the edit page.
            const loaded = await this.persistor.loadData(this.treeId);
            if (!loaded.success) {
                return this.fail(loaded.error);
            }
            // Written as-is: `isValidSkillData` — which the artifact's persistor runs on
            // load — requires the stamped id, name, isPrivate and viewport fields.
            const treeData = loaded.data.treeData;

            this.setState({ step: 'Loading guides…' });
            const guides = new Map<string, string>();
            const missingGuides: string[] = [];
            for (const entry of treeData.documents) {
                if (!documentEntryHasLinkedGuide(entry)) continue;
                // No `preferDraft`: the artifact ships published guides, to match the
                // published tree it is built from.
                const result = await this.persistor.loadDocument(entry.nodeId, this.treeId);
                if (result.success && result.data.content != null) {
                    guides.set(entry.nodeId, result.data.content);
                } else {
                    // A guide that will not load does not stop the deploy; the manifest
                    // records it so the editor can see what is missing from the artifact.
                    missingGuides.push(entry.nodeId);
                }
            }

            this.setState({ step: 'Fetching renderer…' });
            const renderer = await this.fetchRendererAssets();
            if (!renderer) {
                return null;
            }

            this.setState({ step: 'Packaging…' });
            const manifest: DeployManifest = {
                treeId: this.treeId,
                name: treeData.name,
                exportedAt: this.now().toISOString(),
                rendererBuildId: this.rendererBuildId,
                dataVersion: treeData.version,
                guideCount: guides.size,
                missingGuides,
            };

            const files: Record<string, Uint8Array> = {};
            const root = `${this.treeId}/`;
            for (const [name, content] of renderer) {
                files[`${root}${name}`] = strToU8(content);
            }
            files[`${root}data/skill-tree.json`] = strToU8(JSON.stringify(treeData, null, 2));
            for (const [nodeId, markdown] of guides) {
                files[`${root}data/documents/${nodeId}.md`] = strToU8(markdown);
            }
            files[`${root}manifest.json`] = strToU8(JSON.stringify(manifest, null, 2));
            files[`${root}README.md`] = strToU8(buildHostReadme(manifest));

            const zip = zipSync(files);
            this.setState({ phase: 'idle', step: null, error: null });
            return { zip, fileName: `${this.treeId}-skill-tree.zip`, manifest };
        } catch (error) {
            return this.fail(error instanceof Error ? error.message : String(error));
        }
    }

    /** All three or none: a partial renderer would produce an artifact that cannot open. */
    private async fetchRendererAssets(): Promise<Map<string, string> | null> {
        const assets = new Map<string, string>();
        for (const name of RENDERER_ASSETS) {
            const url = `${this.origin}${EMBED_BASE_PATH}/${name}`;
            let response: Response;
            try {
                response = await this.fetchFn(url);
            } catch (error) {
                const message = error instanceof Error ? error.message : String(error);
                return this.fail(`Could not fetch the renderer (${name}): ${message}`);
            }
            if (!response.ok) {
                return this.fail(
                    `Could not fetch the renderer (${name}): ${response.status} ${response.statusText}`,
                );
            }
            assets.set(name, await response.text());
        }
        return assets;
    }

    private fail(error: string): null {
        this.setState({ phase: 'error', step: null, error });
        return null;
    }

    /** Clears an error so the button returns to its normal label. */
    dismissError(): void {
        if (this.state.phase === 'error') {
            this.setState({ phase: 'idle', step: null, error: null });
        }
    }
}

/** The one-screen guide a host reads after unzipping. */
export function buildHostReadme(manifest: DeployManifest): string {
    return `# ${manifest.name}

A deployed skill tree: a read-only renderer plus this tree's data. It needs no server,
no database and no network beyond this folder.

Exported ${manifest.exportedAt} · renderer build \`${manifest.rendererBuildId}\` · ${manifest.guideCount} guide(s).

## Host it

Copy this whole folder into your static directory — in a Vite app that is \`public/\` —
and open \`index.html\` at whatever path it ended up on. Every asset reference is
relative, so the folder works from any subpath. To land on a particular leaf, add
\`?node=<key>\`, the same short key a Skill Trees share link carries.

## Embed it

The simplest embed is an iframe around \`index.html\`.

To place the tree inside a page you already have, import the module and mount it:

\`\`\`html
<div id="tree" style="height: 600px"></div>
<script type="module">
  const url = new URL('./my-tree/skill-tree-viewer.js', location.href).href;
  const { mount } = await import(url);
  mount(document.getElementById('tree'), { dataUrl: './my-tree/data' });
</script>
\`\`\`

**Give the element a height.** The viewer fills whatever box you mount it into; it does
not assume the viewport's height. Width-based layout (the point where it switches to the
narrow, full-width guide panel) still follows the browser window, so a tree mounted in a
narrow column on a wide screen gets the wide layout.

The viewer renders inside a shadow root, so its styles cannot reach your page and yours
cannot reach it — inline mounting needs nothing from you but that height.

\`mount()\` returns a function that unmounts the tree. Options: \`dataUrl\` (required),
\`treeId\`, \`initialNodeKey\`, \`publicUrl\` (base for copy-link affordances; omitted, they
are hidden), \`styles\` (a stylesheet URL — it defaults to \`skill-tree-viewer.css\` beside
the module; pass \`null\` to style the viewer yourself) and \`shadow: false\` (render in the
light DOM instead, where the viewer's stylesheet is global — only sensible when the
viewer owns the whole page).

**If your host is a Vite app**, note that Vite refuses to let application source
\`import\` a JavaScript file out of \`public/\`. Use the runtime import above, whose URL
Vite cannot resolve at build time, rather than a static \`import\` statement.

## Updating

Re-deploy from Skill Trees and replace this folder. There is no migration step. The
filenames are fixed and unhashed, so browsers may hold on to the old renderer or data —
if your host adds cache-busting or versioned paths, apply it here too.

## Notes

- \`data/skill-tree.json\` carries an \`isPrivate\` field from the Skill Trees app. It has
  no meaning here and grants nothing; privacy only governs how skilltrees.app presents a
  tree to its own readers.
- Editing, publishing and guide authoring stay in the Skill Trees app. This artifact is
  view-only.
${
    manifest.missingGuides.length > 0
        ? `- These leaves declare a guide that could not be read at export time, so their
  markdown is missing from \`data/documents/\`: ${manifest.missingGuides.join(', ')}.\n`
        : ''
}`;
}
